import { useState, useEffect, useCallback, useRef, useMemo, useEffectEvent } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { updateMetaTags, MetaConfig } from '../utils/meta'
import {
  generateFromText,
  generateFromImages,
  generateFromFigma,
  getFigmaStatus,
  FigmaConnectionInfo,
  getJiraStatus,
  JiraConnectionInfo,
  fetchJiraIssue,
  searchJiraTickets,
  JiraIssuePreview,
  JiraSearchResult,
  pollJobStatus,
  deleteTestCase,
  toggleStar,
  uploadVideo,
  getJobVideos,
  TestSuite,
  TestCase,
  VideoMetadata,
  STREAMING_URL,
  getGoldenExamples,
  saveGoldenExamples,
} from '../api'
import { exportTestSuite as formatAndExport, ExportFormat } from '../utils/exportFormatters'
import { hasVerifyTags, getVerifyCount, splitVerifySegments, hasVerifyTag } from '../utils/verifyTags'
import { useStreamingGeneration } from '../hooks/useStreamingGeneration'
import TestCaseGeneratorInstructions from './TestCaseGeneratorInstructions'
import AppLayout from '../components/AppLayout'
import HistoryPanel from '../components/HistoryPanel'
import TestCaseHistory from '../components/TestCaseHistory'
import VideoUpload, { UploadedVideo } from '../components/VideoUpload'
import VideoPlayer from '../components/VideoPlayer'

interface UploadedImage {
  base64: string
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
  name: string
}

const MAX_IMAGES = 3
const DEVICE_OPTIONS = ['Desktop', 'Tablet', 'Mobile'] as const
const BROWSER_OPTIONS = ['Chrome', 'Safari', 'Firefox', 'Microsoft Edge'] as const

const colorizeJson = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"([^"\\]*(?:\\.[^"\\]*)*)"\s*:/g, '<span class="json-key">"$1"</span>:')
    .replace(/:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/g, ': <span class="json-string">"$1"</span>')
    .replace(/:\s*(\d+(?:\.\d+)?)/g, ': <span class="json-number">$1</span>')
    .replace(/:\s*(true|false)/g, ': <span class="json-bool">$1</span>')
    .replace(/:\s*(null)/g, ': <span class="json-null">$1</span>')
    .replace(/[{}[\]]/g, '<span class="json-bracket">$&</span>')
}

function extractJiraKey(input: string): string | null {
  const trimmed = input.trim()
  if (/^[A-Z][A-Z0-9]+-\d+$/i.test(trimmed)) return trimmed.toUpperCase()
  const urlMatch = trimmed.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/i)
  if (urlMatch) return urlMatch[1].toUpperCase()
  return null
}

function JiraIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.664 1H10.2603C10.2603 3.99443 12.7697 6.43176 15.8527 6.43176H18.147V8.55567C18.147 11.5501 20.6565 13.9875 23.7395 13.9875V2.04457C23.7395 1.45265 23.2735 1 22.664 1Z" fill="#2684FF"/>
      <path d="M16.5339 6.98877H4.13013C4.13013 9.98321 6.63956 12.4205 9.72258 12.4205H12.0169V14.5793C12.0169 17.5737 14.5264 20.0111 17.6094 20.0111V8.03329C17.6094 7.47623 17.1433 6.98877 16.5339 6.98877Z" fill="url(#jira-tcg-grad-1)"/>
      <path d="M10.4038 13.0125H-1.99622e-05C-1.99622e-05 16.0069 2.50941 18.4442 5.59243 18.4442H7.88677V20.5682C7.88677 23.5626 10.3962 26 13.4792 26V14.057C13.4792 13.4651 12.9774 13.0125 12.4038 13.0125Z" fill="url(#jira-tcg-grad-2)"/>
      <defs>
        <linearGradient id="jira-tcg-grad-1" x1="17.3455" y1="7.01882" x2="12.2302" y2="12.45" gradientUnits="userSpaceOnUse">
          <stop offset="0.176" stopColor="#0052CC"/>
          <stop offset="1" stopColor="#2684FF"/>
        </linearGradient>
        <linearGradient id="jira-tcg-grad-2" x1="11.5601" y1="13.0525" x2="5.63562" y2="18.9874" gradientUnits="userSpaceOnUse">
          <stop offset="0.176" stopColor="#0052CC"/>
          <stop offset="1" stopColor="#2684FF"/>
        </linearGradient>
      </defs>
    </svg>
  )
}

function ToggleChip({ label, checked, onChange, disabled, onDisabledClick }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; onDisabledClick?: () => void }) {
  const isActive = checked && !disabled
  return (
    <button
      type="button"
      onClick={() => { if (disabled) { onDisabledClick?.() } else { onChange(!checked) } }}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors border ${
        isActive
          ? 'bg-[#2684FF]/10 border-[#2684FF]/30 text-[#0052CC]'
          : 'bg-linear-bg-card border-linear-border text-linear-text-muted'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-[#2684FF]/50'}`}
    >
      <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none">
        {isActive ? (
          <rect x="1" y="1" width="14" height="14" rx="3" fill="#2684FF" stroke="#2684FF" strokeWidth="1"/>
        ) : (
          <rect x="1.5" y="1.5" width="13" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
        )}
        {isActive && (
          <path d="M4.5 8L7 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        )}
      </svg>
      {label}
    </button>
  )
}

export default function TestCaseGenerator() {
  const navigate = useNavigate()
  const location = useLocation()

  // Input state
  const [requirement, setRequirement] = useState('')
  const [figmaUrl, setFigmaUrl] = useState('')
  const [figmaStatus, setFigmaStatus] = useState<FigmaConnectionInfo | null>(null)
  const [jiraTicket, setJiraTicket] = useState('')
  const [jiraStatus, setJiraStatus] = useState<JiraConnectionInfo | null>(null)
  const [jiraPreview, setJiraPreview] = useState<JiraIssuePreview | null>(null)
  const [jiraPreviewLoading, setJiraPreviewLoading] = useState(false)
  const [jiraPreviewError, setJiraPreviewError] = useState<string | null>(null)
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [testCaseCount, setTestCaseCount] = useState(10)
  const [autoCount, setAutoCount] = useState(false)
  const [detailLevel, setDetailLevel] = useState<'concise' | 'standard' | 'detailed'>('standard')
  const [targetDevices, setTargetDevices] = useState<string[]>(['all'])
  const [targetBrowsers, setTargetBrowsers] = useState<string[]>(['all'])
  const [customInstructions, setCustomInstructions] = useState('')

  // UI state
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingExisting, setIsLoadingExisting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isNotFound, setIsNotFound] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [showInstructions, setShowInstructions] = useState(false)

  // Results state
  const [testSuite, setTestSuite] = useState<TestSuite | null>(null)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [expandedTestCases, setExpandedTestCases] = useState<Set<string>>(new Set())

  // Delete modal state
  const [deleteModalJobId, setDeleteModalJobId] = useState<string | null>(null)
  const [lastDeletedJobId, setLastDeletedJobId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Video state
  const [pendingVideos, setPendingVideos] = useState<UploadedVideo[]>([])
  const [jobVideos, setJobVideos] = useState<VideoMetadata[]>([])
  const [playingVideo, setPlayingVideo] = useState<VideoMetadata | null>(null)
  const [isUploadingVideos, setIsUploadingVideos] = useState(false)

  // Star/template state
  const [isStarred, setIsStarred] = useState(false)

  // Golden examples state
  const [goldenExampleTitles, setGoldenExampleTitles] = useState<Set<string>>(new Set())
  const [goldenExamplesCount, setGoldenExamplesCount] = useState(0)
  const [savingExampleId, setSavingExampleId] = useState<string | null>(null)

  // Inline editing state
  const [editingTestCase, setEditingTestCase] = useState<string | null>(null)
  const [editedTestCases, setEditedTestCases] = useState<Map<string, TestCase>>(new Map())

  // Selection state for selective export
  const [selectedTestCases, setSelectedTestCases] = useState<Set<string>>(new Set())

  // [VERIFY] tag state
  const [showVerifyOnly, setShowVerifyOnly] = useState(false)
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false)
  const verifyCount = useMemo(() => testSuite ? getVerifyCount(testSuite) : 0, [testSuite])

  // Creator info for detail view
  const [jobCreator, setJobCreator] = useState<{ userName?: string; createdAt?: number } | null>(null)

  // Clone data for "Clone & Edit" feature
  const [cloneData, setCloneData] = useState<{
    requirementText?: string
    jiraTicket?: string
    jiraConfirmed?: boolean
    inputType?: string
    figmaUrl?: string
    testCaseCount?: number
    autoCount?: boolean
    detailLevel?: 'concise' | 'standard' | 'detailed'
    targetDevices?: string[]
    targetBrowsers?: string[]
    customInstructions?: string
  } | null>(null)

  // History panel state
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)
  // Config section collapsed state
  const [configOpen, setConfigOpen] = useState(false)

  // Jira field toggle state for Import from Jira
  const [jiraFieldToggles, setJiraFieldToggles] = useState({
    description: true,
    attachments: false,
  })

  // Jira confirm + search state
  const skipJiraResetRef = useRef(false)
  const [jiraConfirmed, setJiraConfirmed] = useState(false)
  const [jiraSearchResults, setJiraSearchResults] = useState<JiraSearchResult[]>([])
  const [jiraSearchLoading, setJiraSearchLoading] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchToggles, setSearchToggles] = useState<Record<string, typeof jiraFieldToggles>>({})

  // Streaming state
  const streamingOutputRef = useRef<HTMLPreElement>(null)
  const {
    isStreaming,
    streamedText,
    jobId: streamingJobId,
    errorCode: streamErrorCode,
    generate: generateStreaming,
    abort: abortStreaming,
    reset: resetStreaming,
  } = useStreamingGeneration({
    streamingUrl: STREAMING_URL,
    onComplete: async (result, jobId) => {
      setTestSuite(result)
      setCurrentJobId(jobId)
      setCloneData({
        requirementText: requirement || result.requirement || undefined,
        jiraTicket: jiraTicket || undefined,
        jiraConfirmed: jiraConfirmed || undefined,
        inputType: uploadedImages.length > 0 ? 'image' : figmaUrl ? 'figma' : 'text',
        figmaUrl: figmaUrl || undefined,
        testCaseCount,
        autoCount,
        detailLevel,
        targetDevices,
        targetBrowsers,
        customInstructions: customInstructions.trim() || undefined,
      })
      setExpandedTestCases(new Set(result.test_cases.map((tc) => tc.id)))
      // Pass result via navigation state to avoid race condition with backend persistence
      navigate(`/test-case/${jobId}`, {
        state: { testSuite: result, fromStreaming: true },
      })
      setIsLoading(false)
      // Upload any pending videos
      if (pendingVideos.length > 0) {
        uploadPendingVideos(jobId)
      }
    },
    onError: (err) => {
      setError(err)
      setIsLoading(false)
    },
  })

  const isRateLimited = streamErrorCode === 'FIGMA_RATE_LIMIT'

  // Auto-scroll streaming output
  useEffect(() => {
    if (streamingOutputRef.current && streamedText) {
      streamingOutputRef.current.scrollTop = streamingOutputRef.current.scrollHeight
    }
  }, [streamedText])

  // Fetch Figma and Jira connection status on mount
  useEffect(() => {
    getFigmaStatus().then(setFigmaStatus).catch(() => setFigmaStatus({ connected: false, status: 'disconnected' }))
    getJiraStatus().then(setJiraStatus).catch(() => setJiraStatus({ connected: false, status: 'disconnected' }))
  }, [])

  // Fetch golden examples on mount
  useEffect(() => {
    const idToken = localStorage.getItem('idToken')
    if (!idToken) return
    getGoldenExamples()
      .then(({ examples }) => {
        setGoldenExampleTitles(new Set(examples.map((ex) => ex.title)))
        setGoldenExamplesCount(examples.length)
      })
      .catch(() => {}) // best-effort
  }, [])

  // Debounced Jira ticket preview fetch + fuzzy search
  useEffect(() => {
    let stale = false

    // Skip reset when the ticket change came from a confirmed import (search result button)
    if (skipJiraResetRef.current) {
      skipJiraResetRef.current = false
      return
    }

    setJiraPreview(null)
    setJiraPreviewError(null)
    setJiraConfirmed(false)
    setJiraFieldToggles({ description: true, attachments: false })
    setJiraSearchResults([])
    setShowSearchDropdown(false)

    if (!jiraStatus || jiraStatus.status === 'disconnected') return

    const trimmed = jiraTicket.trim()
    if (!trimmed) return

    // Check if input is a URL (exact match, skip search) or text (search)
    const isUrl = trimmed.includes('/browse/')
    const issueKey = extractJiraKey(trimmed)

    const fetchFullPreview = async (key: string) => {
      setJiraPreviewLoading(true)
      try {
        const preview = await fetchJiraIssue(key)
        if (stale) return
        if (!preview.cloudUrl && jiraStatus?.jiraCloudUrl) {
          preview.cloudUrl = jiraStatus.jiraCloudUrl
        }
        setJiraPreview(preview)
        setJiraPreviewError(null)
        setShowSearchDropdown(false)
        setJiraSearchResults([])
      } catch (err) {
        setJiraPreview(null)
        const msg = err instanceof Error ? err.message : 'Failed to fetch ticket'
        if (msg.includes('expired')) {
          setJiraPreviewError('Your Jira connection has expired. Reconnect in Settings to continue.')
          setJiraStatus({ connected: false, status: 'expired' })
        } else if (msg.includes('not connected')) {
          setJiraPreviewError('Connect your Jira account in Settings to fetch ticket details.')
          setJiraStatus({ connected: false, status: 'disconnected' })
        } else {
          setJiraPreviewError(msg)
        }
      } finally {
        setJiraPreviewLoading(false)
      }
    }

    if (isUrl && issueKey) {
      // URL: fetch single preview directly, no search
      const timer = setTimeout(() => fetchFullPreview(issueKey), 500)
      return () => { stale = true; clearTimeout(timer); setJiraPreviewLoading(false) }
    } else if (trimmed.length >= 3) {
      // Search for all inputs 3+ chars (including exact keys like NEL-20)
      const timer = setTimeout(async () => {
        setJiraSearchLoading(true)
        try {
          const { results, hasMore } = await searchJiraTickets(trimmed)
          if (stale) return
          setJiraSearchResults(results)
          setHasMoreSearchResults(!!hasMore)
          setShowSearchDropdown(results.length > 0)
          // Initialize per-result toggles
          const defaultToggles = { description: true, attachments: false }
          const newToggles: Record<string, typeof defaultToggles> = {}
          results.forEach(r => { newToggles[r.key] = { ...defaultToggles } })
          setSearchToggles(newToggles)
        } catch {
          setJiraSearchResults([])
          setShowSearchDropdown(false)
        } finally {
          setJiraSearchLoading(false)
        }
      }, 500)
      return () => { stale = true; clearTimeout(timer); setJiraSearchLoading(false) }
    }
  }, [jiraTicket, jiraStatus])

  // Initialize selection and reset verify banner when suite changes
  useEffect(() => {
    if (testSuite) {
      setSelectedTestCases(new Set(testSuite.test_cases.map((tc) => tc.id)))
      setVerifyBannerDismissed(false)
      setShowVerifyOnly(false)
    } else {
      setSelectedTestCases(new Set())
    }
  }, [testSuite])

  // Clear error when user makes changes
  const clearError = useCallback(() => {
    if (error) setError(null)
  }, [error])

  // Show toast notification
  const showToast = (message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 3000)
  }

  // Save a single test case as a golden example
  const handleSaveAsExample = async (tc: TestCase) => {
    if (!currentJobId) return
    setSavingExampleId(tc.id)
    try {
      await saveGoldenExamples([tc], currentJobId)
      setGoldenExampleTitles((prev) => new Set([...prev, tc.title]))
      setGoldenExamplesCount((prev) => prev + 1)
      showToast('Saved as golden example')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save example')
    } finally {
      setSavingExampleId(null)
    }
  }

  // Bulk save selected test cases as golden examples
  const handleBulkSaveAsExamples = async () => {
    if (!currentJobId || !testSuite || selectedTestCases.size === 0) return
    const selected = testSuite.test_cases.filter((tc) => selectedTestCases.has(tc.id))
    const unsaved = selected.filter((tc) => !goldenExampleTitles.has(tc.title))
    if (unsaved.length === 0) {
      showToast('All selected test cases are already saved as examples')
      return
    }
    try {
      await saveGoldenExamples(unsaved, currentJobId)
      setGoldenExampleTitles((prev) => {
        const next = new Set(prev)
        unsaved.forEach((tc) => next.add(tc.title))
        return next
      })
      setGoldenExamplesCount((prev) => prev + unsaved.length)
      showToast(`Saved ${unsaved.length} test case${unsaved.length > 1 ? 's' : ''} as golden example${unsaved.length > 1 ? 's' : ''}`)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save examples')
    }
  }

  // Upload pending videos to a job
  const uploadPendingVideos = async (jobId: string) => {
    if (pendingVideos.length === 0) return

    setIsUploadingVideos(true)
    const uploadedVideos: VideoMetadata[] = []

    for (let i = 0; i < pendingVideos.length; i++) {
      const video = pendingVideos[i]
      try {
        // Update status to uploading
        setPendingVideos((prev) =>
          prev.map((v, idx) => (idx === i ? { ...v, status: 'uploading' as const } : v))
        )

        const result = await uploadVideo(jobId, video.file, (progress) => {
          setPendingVideos((prev) =>
            prev.map((v, idx) => (idx === i ? { ...v, progress } : v))
          )
        })

        uploadedVideos.push(result)

        // Update status to complete
        setPendingVideos((prev) =>
          prev.map((v, idx) => (idx === i ? { ...v, status: 'complete' as const, videoId: result.videoId } : v))
        )
      } catch (err) {
        console.error('Failed to upload video:', err)
        setPendingVideos((prev) =>
          prev.map((v, idx) =>
            idx === i ? { ...v, status: 'error' as const, error: 'Upload failed' } : v
          )
        )
      }
    }

    // Fetch the updated videos list from server to get download URLs
    try {
      const videosResult = await getJobVideos(jobId)
      setJobVideos(videosResult.videos)
    } catch (err) {
      console.warn('Failed to fetch videos after upload:', err)
    }

    // Clear pending videos after all uploads complete
    setPendingVideos([])
    setIsUploadingVideos(false)

    if (uploadedVideos.length > 0) {
      showToast(`${uploadedVideos.length} video${uploadedVideos.length > 1 ? 's' : ''} uploaded`)
    }
  }

  // Handle delete test case
  const handleDeleteTestCase = async (jobId: string) => {
    setIsDeleting(true)
    try {
      await deleteTestCase(jobId)
      setLastDeletedJobId(jobId)

      // If we're viewing this job's results, navigate back to form
      if (currentJobId === jobId) {
        setTestSuite(null)
        setCurrentJobId(null)
        setJobCreator(null)
        setCloneData(null)
        setIsStarred(false)
        setEditingTestCase(null)
        setEditedTestCases(new Map())
        navigate('/test-case/')
      }

      setDeleteModalJobId(null)
      showToast('Test cases deleted successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete test cases')
    } finally {
      setIsDeleting(false)
    }
  }

  // Check for instructions or share URL on location change
  const onRouteChange = useEffectEvent(() => {
    const path = location.pathname

    // Check if on instructions page
    if (path.includes('/instructions')) {
      setShowInstructions(true)
      return
    }

    setShowInstructions(false)

    // Check for share URL
    const match = path.match(/\/test-case\/([a-f0-9-]+)/)
    if (match) {
      const jobId = match[1]
      setCurrentJobId(jobId)

      // Check if result was passed via navigation state (from streaming completion)
      const navState = location.state as { testSuite?: TestSuite; fromStreaming?: boolean } | null
      if (navState?.fromStreaming && navState?.testSuite) {
        // We already have the data from streaming - no need to fetch from backend
        setTestSuite(navState.testSuite)
        setExpandedTestCases(new Set(navState.testSuite.test_cases.map((tc) => tc.id)))
        // Clear navigation state to avoid stale data on page refresh
        window.history.replaceState({}, document.title)
        return
      }

      // Otherwise, load from backend (shared URL or page refresh)
      loadExistingJob(jobId)
    } else if (path === '/test-case' || path === '/test-case/') {
      const navState = location.state as { cloneFrom?: { requirementText: string; jiraTicket: string; jiraConfirmed?: boolean; figmaUrl?: string; testCaseCount?: number; autoCount?: boolean; detailLevel?: 'concise' | 'standard' | 'detailed'; targetDevices?: string[]; targetBrowsers?: string[]; customInstructions?: string } } | null

      if (navState?.cloneFrom) {
        // Pre-fill form with cloned data
        setRequirement(navState.cloneFrom.requirementText || '')
        setJiraTicket(navState.cloneFrom.jiraTicket || '')
        if (navState.cloneFrom.figmaUrl) setFigmaUrl(navState.cloneFrom.figmaUrl)
        // Note: jiraConfirmed will be set by the debounce effect after preview loads
        if (navState.cloneFrom.testCaseCount) setTestCaseCount(navState.cloneFrom.testCaseCount)
        if (navState.cloneFrom.autoCount !== undefined) setAutoCount(navState.cloneFrom.autoCount)
        if (navState.cloneFrom.detailLevel) setDetailLevel(navState.cloneFrom.detailLevel)
        if (navState.cloneFrom.targetDevices) setTargetDevices(navState.cloneFrom.targetDevices)
        if (navState.cloneFrom.targetBrowsers) setTargetBrowsers(navState.cloneFrom.targetBrowsers)
        if (navState.cloneFrom.customInstructions) setCustomInstructions(navState.cloneFrom.customInstructions)
        // Clear navigation state to prevent re-filling on refresh
        window.history.replaceState({}, document.title)
        // Reset result state
        setTestSuite(null)
        setCurrentJobId(null)
        setJobCreator(null)
        setCloneData(null)
        setError(null)
        setIsNotFound(false)
        setIsStarred(false)
        setEditingTestCase(null)
        setEditedTestCases(new Map())
      } else if (testSuite || currentJobId) {
        // Navigating back to home - reset all state
        setTestSuite(null)
        setCurrentJobId(null)
        setJobCreator(null)
        setCloneData(null)
        setError(null)
        setIsNotFound(false)
        setIsStarred(false)
        setEditingTestCase(null)
        setEditedTestCases(new Map())
        // Also reset form fields
        setRequirement('')
        setUploadedImages([])
        setFigmaUrl('')
        setJiraTicket('')
        setJiraConfirmed(false)
        setJiraSearchResults([])
        setShowSearchDropdown(false)
        setPendingVideos([])
        setJobVideos([])
        setTargetDevices(['all'])
        setTargetBrowsers(['all'])
      }
    }
  })
  useEffect(() => {
    onRouteChange()
  }, [location.pathname, location.state])

  // Update meta tags
  useEffect(() => {
    if (testSuite) {
      const totalTests = testSuite.test_cases.length
      const happyPaths = testSuite.test_cases.filter((tc) => tc.type === 'happy_path').length
      const sadPaths = testSuite.test_cases.filter((tc) => tc.type === 'sad_path').length
      const edgeCases = testSuite.test_cases.filter((tc) => tc.type === 'edge_case').length

      updateMetaTags(
        `Test Cases: ${testSuite.test_suite_name}`,
        `${totalTests} test cases | ${happyPaths} Happy Path | ${sadPaths} Sad Path | ${edgeCases} Edge Case`,
        window.location.href
      )
    } else {
      updateMetaTags(
        MetaConfig.testCase.title,
        MetaConfig.testCase.description,
        window.location.href
      )
    }
  }, [testSuite])

  const loadExistingJob = async (jobId: string) => {
    // Clear editing state to prevent bleed between jobs
    setEditingTestCase(null)
    setEditedTestCases(new Map())
    try {
      setIsLoadingExisting(true)
      const status = await pollJobStatus(
        jobId,
        (status) => {
          setProgress(status.progress)
          setStatusMessage(status.statusMessage || '')
        },
        2000,
        1 // Only check once for existing jobs
      )

      if (status.status === 'completed' && status.testSuite) {
        setTestSuite(status.testSuite)
        // Expand all test cases by default
        setExpandedTestCases(new Set(status.testSuite.test_cases.map((tc) => tc.id)))
        // Store creator info
        setJobCreator({ userName: status.userName, createdAt: status.createdAt })
        setIsStarred(status.starred || false)
        setJiraTicket(status.jiraTicket || '')
        setCloneData({
          requirementText: status.requirementText || status.testSuite?.requirement,
          jiraTicket: status.jiraTicket,
          inputType: status.inputType,
          figmaUrl: status.figmaUrl,
          testCaseCount: status.testCaseCount,
          detailLevel: status.detailLevel,
          targetDevices: status.targetDevices,
          targetBrowsers: status.targetBrowsers,
        })
        // Fetch videos for this job
        try {
          const videosResult = await getJobVideos(jobId)
          setJobVideos(videosResult.videos)
        } catch (videoErr) {
          console.warn('Failed to load videos:', videoErr)
        }
      } else if (status.status === 'failed') {
        setError(status.error || 'Job failed')
      } else if (status.status === 'processing' || status.status === 'pending') {
        // Continue polling - show loading indicator for in-progress jobs
        setIsLoading(true)
        const finalStatus = await pollJobStatus(jobId, (status) => {
          setProgress(status.progress)
          setStatusMessage(status.statusMessage || '')
        })

        if (finalStatus.status === 'completed' && finalStatus.testSuite) {
          setTestSuite(finalStatus.testSuite)
          setExpandedTestCases(new Set(finalStatus.testSuite.test_cases.map((tc) => tc.id)))
          setJobCreator({ userName: finalStatus.userName, createdAt: finalStatus.createdAt })
          setIsStarred(finalStatus.starred || false)
          setJiraTicket(finalStatus.jiraTicket || '')
          setCloneData({
            requirementText: finalStatus.requirementText || finalStatus.testSuite?.requirement,
            jiraTicket: finalStatus.jiraTicket,
            inputType: finalStatus.inputType,
            figmaUrl: finalStatus.figmaUrl,
            testCaseCount: finalStatus.testCaseCount,
            detailLevel: finalStatus.detailLevel,
            targetDevices: finalStatus.targetDevices,
            targetBrowsers: finalStatus.targetBrowsers,
          })
          // Fetch videos for this job
          try {
            const videosResult = await getJobVideos(jobId)
            setJobVideos(videosResult.videos)
          } catch (videoErr) {
            console.warn('Failed to load videos:', videoErr)
          }
        } else if (finalStatus.status === 'failed') {
          setError(finalStatus.error || 'Job failed')
        }
        setIsLoading(false)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load job'
      // Check if it's a not found error
      if (errorMessage.includes('not found') || errorMessage.includes('404') || errorMessage.includes('Job not found')) {
        setIsNotFound(true)
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsLoadingExisting(false)
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageFiles: File[] = []
    const videoFiles: File[] = []

    acceptedFiles.forEach((file) => {
      if (file.type.startsWith('video/')) {
        videoFiles.push(file)
      } else {
        imageFiles.push(file)
      }
    })

    // Process images - limit to remaining slots
    const remainingSlots = MAX_IMAGES - uploadedImages.length
    const imagesToProcess = imageFiles.slice(0, remainingSlots)

    imagesToProcess.forEach((file) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        const mediaType = file.type as 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
        setUploadedImages((prev) => {
          if (prev.length >= MAX_IMAGES) return prev
          return [...prev, { base64, mediaType, name: file.name }]
        })
        clearError()
      }
      reader.readAsDataURL(file)
    })

    // Process videos - add to pending videos
    if (videoFiles.length > 0) {
      const newPendingVideos: UploadedVideo[] = videoFiles.map((file) => ({
        file,
        status: 'pending' as const,
        progress: 0,
      }))
      setPendingVideos((prev) => [...prev, ...newPendingVideos])
      clearError()
    }
  }, [clearError, uploadedImages.length])

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index))
  }

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
    },
    noClick: true,
    noKeyboard: true,
  })

  // Cancel generation and clean up the partially-created job
  const handleCancelGeneration = async () => {
    const jobIdToDelete = streamingJobId || currentJobId
    abortStreaming()
    setIsLoading(false)
    setStatusMessage('')
    setError('Generation cancelled by user.')
    // Delete the incomplete job so it doesn't appear in history
    if (jobIdToDelete) {
      try {
        await deleteTestCase(jobIdToDelete)
      } catch (err) {
        // Best-effort deletion — job may not exist yet or may have already completed
        console.warn('Failed to delete cancelled job:', err)
      }
    }
  }

  const handleGenerate = async () => {
    setError(null)
    setIsLoading(true)
    setProgress(0)
    setStatusMessage('Starting generation...')
    resetStreaming()

    // Determine which input(s) to use
    const hasText = requirement.trim().length > 0
    const hasImages = uploadedImages.length > 0
    const hasFigma = figmaUrl.trim().length > 0
    // Jira counts as valid input only when it provides content (description or attachments toggled on and available)
    const hasJiraContent = jiraConfirmed && jiraPreview !== null && (
      (jiraFieldToggles.description && !!jiraPreview.description) ||
      (jiraFieldToggles.attachments && (jiraPreview.attachments?.length ?? 0) > 0)
    )
    const hasJira = hasJiraContent

    if (!hasText && !hasImages && !hasFigma && !hasJira) {
      setError('Please provide requirements, upload media, enter a Figma URL, or import a Jira ticket')
      setIsLoading(false)
      return
    }

    // Build device/browser context suffix
    const deviceBrowserContext = []
    if (!targetDevices.includes('all')) {
      deviceBrowserContext.push(`Target devices: ${targetDevices.join(', ')}`)
    }
    if (!targetBrowsers.includes('all')) {
      deviceBrowserContext.push(`Target browsers: ${targetBrowsers.join(', ')}`)
    }
    const deviceBrowserSuffix = deviceBrowserContext.length > 0 ? `\n\n${deviceBrowserContext.join('\n')}` : ''

    // Use streaming for all input types (Figma design fetch happens server-side before streaming)
    const canUseStreaming = !!STREAMING_URL

    if (canUseStreaming) {
      // Use streaming generation
      const images = hasImages
        ? uploadedImages.map((img) => ({ base64: img.base64, mediaType: img.mediaType }))
        : undefined

      generateStreaming({
        requirement: [hasText ? requirement : '', deviceBrowserSuffix].filter(Boolean).join('\n\n') || undefined,
        images,
        additionalContext: [hasText && hasImages ? requirement : '', deviceBrowserSuffix].filter(Boolean).join('\n\n') || undefined,
        customInstructions: customInstructions.trim() || undefined,
        jiraTicket: jiraTicket || undefined,
        jiraIncludeFields: hasJira ? jiraFieldToggles : undefined,
        figmaUrl: hasFigma ? figmaUrl : undefined,
        testCaseCount,
        detailLevel,
        autoCount,
        targetDevices,
        targetBrowsers,
      })
      // The rest is handled by onComplete/onError callbacks
      return
    }

    // Fallback to polling-based generation
    try {
      let response

      // Priority: Images > Figma > Text (but text can supplement others)
      if (hasImages) {
        // Use images with text as additional context
        response = await generateFromImages(
          uploadedImages,
          [hasText ? requirement : '', deviceBrowserSuffix].filter(Boolean).join('\n\n') || undefined,
          jiraTicket || undefined,
          testCaseCount,
          detailLevel,
          customInstructions.trim() || undefined,
          hasJira ? jiraFieldToggles : undefined,
          autoCount
        )
      } else if (hasFigma) {
        // Use Figma URL with text as additional context
        response = await generateFromFigma(
          figmaUrl,
          [hasText ? requirement : '', deviceBrowserSuffix].filter(Boolean).join('\n\n') || undefined,
          jiraTicket || undefined,
          testCaseCount,
          detailLevel,
          customInstructions.trim() || undefined,
          hasJira ? jiraFieldToggles : undefined,
          autoCount
        )
      } else {
        // Text only
        response = await generateFromText(requirement + deviceBrowserSuffix, jiraTicket || undefined, testCaseCount, detailLevel, customInstructions.trim() || undefined, hasJira ? jiraFieldToggles : undefined, autoCount)
      }

      setCurrentJobId(response.jobId)

      if (response.testSuite) {
        // Completed synchronously
        setTestSuite(response.testSuite)
        setExpandedTestCases(new Set(response.testSuite.test_cases.map((tc) => tc.id)))
        setCloneData({
          requirementText: requirement || undefined,
          jiraTicket: jiraTicket || undefined,
          jiraConfirmed: jiraConfirmed || undefined,
          inputType: uploadedImages.length > 0 ? 'image' : figmaUrl ? 'figma' : 'text',
          figmaUrl: figmaUrl || undefined,
          testCaseCount,
          autoCount,
          detailLevel,
          targetDevices,
          targetBrowsers,
          customInstructions: customInstructions.trim() || undefined,
        })
        setProgress(100)
        // Update URL only after completion
        navigate(`/test-case/${response.jobId}`)
        // Upload any pending videos
        if (pendingVideos.length > 0) {
          uploadPendingVideos(response.jobId)
        }
      } else {
        // Poll for completion
        const finalStatus = await pollJobStatus(response.jobId, (status) => {
          setProgress(status.progress)
          setStatusMessage(status.statusMessage || '')
        })

        if (finalStatus.status === 'completed' && finalStatus.testSuite) {
          setTestSuite(finalStatus.testSuite)
          setExpandedTestCases(new Set(finalStatus.testSuite.test_cases.map((tc) => tc.id)))
          setCloneData({
            requirementText: requirement || undefined,
            jiraTicket: jiraTicket || undefined,
            jiraConfirmed: jiraConfirmed || undefined,
            inputType: uploadedImages.length > 0 ? 'image' : figmaUrl ? 'figma' : 'text',
            figmaUrl: figmaUrl || undefined,
            testCaseCount,
            autoCount,
            detailLevel,
            targetDevices,
            targetBrowsers,
            customInstructions: customInstructions.trim() || undefined,
          })
          // Update URL only after completion
          navigate(`/test-case/${response.jobId}`)
          // Upload any pending videos
          if (pendingVideos.length > 0) {
            uploadPendingVideos(response.jobId)
          }
        } else if (finalStatus.status === 'failed') {
          throw new Error(finalStatus.error || 'Generation failed')
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred'
      if (message.includes('figma_expired') || message.includes('token expired')) {
        setError('Your Figma connection has expired. Please reconnect in Settings.')
        setFigmaStatus({ connected: false, status: 'expired' })
      } else if (message.includes('figma_not_connected') || message.includes('Figma not connected')) {
        setError('Figma is not connected. Please connect your account in Settings.')
        setFigmaStatus({ connected: false, status: 'disconnected' })
      } else {
        setError(message)
      }
    } finally {
      setIsLoading(false)
      setStatusMessage('')
    }
  }

  const handleExport = (format: ExportFormat) => {
    if (!testSuite || selectedTestCases.size === 0) return

    try {
      const suiteToExport = selectedTestCases.size === testSuite.test_cases.length
        ? testSuite
        : { ...testSuite, test_cases: testSuite.test_cases.filter((tc) => selectedTestCases.has(tc.id)) }
      const content = formatAndExport(suiteToExport, format, jiraTicket || undefined)
      const mimeType = format === 'json' ? 'application/json' : format === 'markdown' ? 'text/markdown' : 'text/csv'
      const blob = new Blob([content], { type: `${mimeType};charset=utf-8` })
      const url = URL.createObjectURL(blob)
      try {
        const a = document.createElement('a')
        a.href = url

        const platformNames: Record<string, string> = {
          testrail: 'testrail',
          zephyr: 'zephyr_scale',
          jira: 'jira',
          markdown: 'markdown',
          json: 'json',
        }
        const extension = format === 'markdown' ? 'md' : format === 'json' ? 'json' : 'csv'
        a.download = `test_cases_${platformNames[format]}.${extension}`

        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        showToast(`Exported as ${platformNames[format].replace('_', ' ').toUpperCase()}`)
      } finally {
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  const toggleTestCase = (id: string) => {
    setExpandedTestCases((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const copyShareUrl = () => {
    const url = window.location.href
    navigator.clipboard.writeText(url).then(() => {
      showToast('Share URL copied to clipboard!')
    }).catch(() => {
      showToast('Failed to copy URL')
    })
  }

  // Sort test cases by type (happy_path first, then sad_path, then edge_case) and priority within each type
  const sortTestCases = (testCases: TestCase[]): TestCase[] => {
    const typeOrder = { happy_path: 0, sad_path: 1, edge_case: 2 }
    const priorityOrder = { high: 0, medium: 1, low: 2 }

    return [...testCases].sort((a, b) => {
      // First sort by type
      const typeCompare = typeOrder[a.type] - typeOrder[b.type]
      if (typeCompare !== 0) return typeCompare

      // Then sort by priority within the same type
      return priorityOrder[a.priority] - priorityOrder[b.priority]
    })
  }

  const renderTestCase = (tc: TestCase, index: number) => {
    const isExpanded = expandedTestCases.has(tc.id)
    const borderColorMap = {
      happy_path: 'border-l-success',
      sad_path: 'border-l-error',
      edge_case: 'border-l-warning',
    }
    const typeBadgeMap = {
      happy_path: { label: 'Happy Path', classes: 'bg-success/15 text-success border border-success/30' },
      sad_path: { label: 'Sad Path', classes: 'bg-error/15 text-error border border-error/30' },
      edge_case: { label: 'Edge Case', classes: 'bg-warning/15 text-warning border border-warning/30' },
    }
    const priorityBadgeMap: Record<string, { label: string; classes: string }> = {
      critical: { label: 'High', classes: 'text-error bg-error/15 border border-error/30' },
      high: { label: 'High', classes: 'text-error bg-error/15 border border-error/30' },
      medium: { label: 'Medium', classes: 'text-info bg-info/15 border border-info/30' },
      low: { label: 'Low', classes: 'text-linear-text-muted bg-linear-text-muted/15 border border-linear-text-muted/30' },
    }
    const typeBadge = typeBadgeMap[tc.type]
    const priorityBadge = priorityBadgeMap[tc.priority] || priorityBadgeMap.medium
    const paddedIndex = String(index + 1).padStart(2, '0')

    return (
      <div key={tc.id} className={`bg-linear-bg-sidebar border-y border-r border-linear-border rounded-md overflow-hidden shadow-sm border-l-4 ${borderColorMap[tc.type]}`}>
        {/* Header row */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-linear-bg-hover transition-colors group"
          onClick={() => toggleTestCase(tc.id)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTestCase(tc.id) } }}
        >
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <input
              type="checkbox"
              checked={selectedTestCases.has(tc.id)}
              onChange={() => {
                setSelectedTestCases((prev) => {
                  const next = new Set(prev)
                  if (next.has(tc.id)) next.delete(tc.id)
                  else next.add(tc.id)
                  return next
                })
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-linear-border text-linear-accent focus:ring-linear-accent flex-shrink-0 cursor-pointer"
              aria-label={`Select ${tc.title}`}
            />
            <div className="w-6 h-6 flex items-center justify-center rounded bg-linear-bg border border-linear-border text-xs font-mono text-linear-text-muted flex-shrink-0">
              {paddedIndex}
            </div>
            <div className="flex flex-col min-w-0">
              <h3 className={`text-sm font-medium text-linear-text ${!isExpanded ? 'truncate' : ''}`} title={tc.title}>
                {tc.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeBadge.classes}`}>
                  {typeBadge.label}
                </span>
                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${priorityBadge.classes}`}>
                  {priorityBadge.label}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Golden Example bookmark */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (!goldenExampleTitles.has(tc.title)) {
                  handleSaveAsExample(tc)
                }
              }}
              disabled={goldenExampleTitles.has(tc.title) || savingExampleId === tc.id}
              className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${
                goldenExampleTitles.has(tc.title)
                  ? 'text-linear-yellow !opacity-100 cursor-default'
                  : 'text-linear-text-muted hover:text-linear-yellow'
              }`}
              title={goldenExampleTitles.has(tc.title) ? 'Already a golden example' : 'Save as golden example'}
              aria-label={goldenExampleTitles.has(tc.title) ? 'Already a golden example' : 'Save as golden example'}
            >
              <svg className="w-4 h-4" fill={goldenExampleTitles.has(tc.title) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </button>
            {editingTestCase !== tc.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setEditingTestCase(tc.id)
                  setEditedTestCases((prev) => new Map(prev).set(tc.id, { ...tc }))
                  if (!isExpanded) toggleTestCase(tc.id)
                }}
                className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-linear-border text-linear-text-secondary transition-all"
                title="Edit Inline"
              >
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
            <svg
              className={`w-5 h-5 text-linear-text-muted group-hover:text-linear-text transition-all ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="border-t border-linear-border bg-linear-bg-card/30 p-4 animate-fade-in">
            {/* Edit mode controls */}
            {editingTestCase === tc.id && (
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 mr-4">
                  <label className="label text-xs">Title</label>
                  <input
                    type="text"
                    value={editedTestCases.get(tc.id)?.title || ''}
                    onChange={(e) => {
                      setEditedTestCases((prev) => {
                        const map = new Map(prev)
                        const current = map.get(tc.id)
                        if (current) map.set(tc.id, { ...current, title: e.target.value })
                        return map
                      })
                    }}
                    className="input w-full text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 self-end">
                  <button
                    onClick={() => {
                      setEditedTestCases((prev) => {
                        const map = new Map(prev)
                        map.delete(tc.id)
                        return map
                      })
                      setEditingTestCase(null)
                    }}
                    className="btn btn-ghost text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const edited = editedTestCases.get(tc.id)
                      if (edited && testSuite) {
                        const cleaned = {
                          ...edited,
                          preconditions: edited.preconditions.filter((p) => p.trim() !== ''),
                          steps: edited.steps.length > 0 ? edited.steps : [{ step_number: 1, action: '', test_data: '', expected_result: '' }],
                        }
                        setTestSuite({
                          ...testSuite,
                          test_cases: testSuite.test_cases.map((t) => t.id === tc.id ? cleaned : t),
                        })
                      }
                      setEditingTestCase(null)
                      showToast('Test case updated')
                    }}
                    className="btn btn-primary text-xs"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Tags */}
            {tc.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {tc.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 rounded text-xs bg-linear-bg-card border border-linear-border text-linear-text-secondary">
                    #{tag.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}

            {/* Preconditions */}
            {editingTestCase === tc.id ? (
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Preconditions</h4>
                <textarea
                  value={(editedTestCases.get(tc.id)?.preconditions || []).join('\n')}
                  onChange={(e) => {
                    setEditedTestCases((prev) => {
                      const map = new Map(prev)
                      const current = map.get(tc.id)
                      if (current) map.set(tc.id, { ...current, preconditions: e.target.value.split('\n') })
                      return map
                    })
                  }}
                  className="textarea w-full text-sm"
                  rows={3}
                />
              </div>
            ) : (
              tc.preconditions.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Preconditions</h4>
                  <ul className="list-disc list-inside text-sm text-linear-text-secondary space-y-1 ml-1">
                    {tc.preconditions.map((pre, idx) => (
                      <li key={idx}>{pre}</li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Test Steps */}
            {editingTestCase === tc.id ? (
              <div>
                <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Test Steps</h4>
                <div className="overflow-hidden rounded-md border border-linear-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-linear-bg-card text-linear-text-muted text-xs uppercase font-semibold">
                      <tr>
                        <th className="px-4 py-2 w-12 text-center">#</th>
                        <th className="px-4 py-2 w-1/3">Action</th>
                        <th className="px-4 py-2 w-1/4">Test Data</th>
                        <th className="px-4 py-2">Expected Result</th>
                        <th className="px-4 py-2 w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-linear-border">
                      {(editedTestCases.get(tc.id)?.steps || []).map((step, idx, allSteps) => (
                        <tr key={`edit-step-${idx}`} className="bg-linear-bg-sidebar">
                          <td className="px-4 py-3 text-center text-linear-text-muted">{step.step_number}</td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={step.action}
                              onChange={(e) => {
                                setEditedTestCases((prev) => {
                                  const map = new Map(prev)
                                  const current = map.get(tc.id)
                                  if (current) {
                                    const steps = [...current.steps]
                                    steps[idx] = { ...steps[idx], action: e.target.value }
                                    map.set(tc.id, { ...current, steps })
                                  }
                                  return map
                                })
                              }}
                              className="input w-full text-sm py-1"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={step.test_data || ''}
                              onChange={(e) => {
                                setEditedTestCases((prev) => {
                                  const map = new Map(prev)
                                  const current = map.get(tc.id)
                                  if (current) {
                                    const steps = [...current.steps]
                                    steps[idx] = { ...steps[idx], test_data: e.target.value }
                                    map.set(tc.id, { ...current, steps })
                                  }
                                  return map
                                })
                              }}
                              className="input w-full text-sm py-1"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={step.expected_result}
                              onChange={(e) => {
                                setEditedTestCases((prev) => {
                                  const map = new Map(prev)
                                  const current = map.get(tc.id)
                                  if (current) {
                                    const steps = [...current.steps]
                                    steps[idx] = { ...steps[idx], expected_result: e.target.value }
                                    map.set(tc.id, { ...current, steps })
                                  }
                                  return map
                                })
                              }}
                              className="input w-full text-sm py-1"
                            />
                          </td>
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={() => {
                                  setEditedTestCases((prev) => {
                                    const map = new Map(prev)
                                    const current = map.get(tc.id)
                                    if (current && idx > 0) {
                                      const steps = [...current.steps]
                                      ;[steps[idx - 1], steps[idx]] = [steps[idx], steps[idx - 1]]
                                      map.set(tc.id, { ...current, steps: steps.map((s, i) => ({ ...s, step_number: i + 1 })) })
                                    }
                                    return map
                                  })
                                }}
                                disabled={idx === 0}
                                className="p-1 text-linear-text-muted hover:text-linear-accent disabled:opacity-25 disabled:cursor-default transition-colors"
                                title="Move step up"
                                aria-label="Move step up"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditedTestCases((prev) => {
                                    const map = new Map(prev)
                                    const current = map.get(tc.id)
                                    if (current && idx < current.steps.length - 1) {
                                      const steps = [...current.steps]
                                      ;[steps[idx], steps[idx + 1]] = [steps[idx + 1], steps[idx]]
                                      map.set(tc.id, { ...current, steps: steps.map((s, i) => ({ ...s, step_number: i + 1 })) })
                                    }
                                    return map
                                  })
                                }}
                                disabled={idx === allSteps.length - 1}
                                className="p-1 text-linear-text-muted hover:text-linear-accent disabled:opacity-25 disabled:cursor-default transition-colors"
                                title="Move step down"
                                aria-label="Move step down"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditedTestCases((prev) => {
                                    const map = new Map(prev)
                                    const current = map.get(tc.id)
                                    if (current) {
                                      const steps = current.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }))
                                      map.set(tc.id, { ...current, steps })
                                    }
                                    return map
                                  })
                                }}
                                className="p-1 text-linear-text-muted hover:text-error transition-colors"
                                title="Remove step"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => {
                    setEditedTestCases((prev) => {
                      const map = new Map(prev)
                      const current = map.get(tc.id)
                      if (current) {
                        const newStep = {
                          step_number: current.steps.length + 1,
                          action: '',
                          test_data: '',
                          expected_result: '',
                        }
                        map.set(tc.id, { ...current, steps: [...current.steps, newStep] })
                      }
                      return map
                    })
                  }}
                  className="mt-2 text-xs text-linear-accent hover:text-linear-accent-hover font-medium flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Step
                </button>
              </div>
            ) : (
              <div>
                <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Test Steps</h4>
                <div className="overflow-hidden rounded-md border border-linear-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-linear-bg-card text-linear-text-muted text-xs uppercase font-semibold">
                      <tr>
                        <th className="px-4 py-2 w-12 text-center">#</th>
                        <th className="px-4 py-2 w-1/3">Action</th>
                        <th className="px-4 py-2 w-1/4">Test Data</th>
                        <th className="px-4 py-2">Expected Result</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-linear-border">
                      {tc.steps.map((step) => (
                        <tr key={step.step_number} className="bg-linear-bg-sidebar">
                          <td className="px-4 py-3 text-center text-linear-text-muted">{step.step_number}</td>
                          <td className="px-4 py-3 text-linear-text-secondary">{step.action}</td>
                          <td className="px-4 py-3 text-linear-text-muted italic">{step.test_data || '-'}</td>
                          <td className="px-4 py-3 text-linear-text-secondary">
                            {hasVerifyTag(step.expected_result)
                              ? splitVerifySegments(step.expected_result).map((seg, i) =>
                                  seg.isVerify ? (
                                    <button
                                      key={i}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setEditingTestCase(tc.id)
                                        setEditedTestCases((prev) => {
                                          const map = new Map(prev)
                                          if (!map.has(tc.id)) map.set(tc.id, { ...tc })
                                          return map
                                        })
                                      }}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-warning/15 text-warning border border-warning/30 hover:bg-warning/25 cursor-pointer transition-colors"
                                      title="Click to edit and resolve this ambiguity"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                      {seg.text}
                                    </button>
                                  ) : (
                                    <span key={i}>{seg.text}</span>
                                  )
                                )
                              : step.expected_result}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Help handler

  // Show instructions view if requested
  if (showInstructions) {
    return (
      <TestCaseGeneratorInstructions
        onBack={() => setShowInstructions(false)}
      />
    )
  }

  // Build breadcrumbs based on current state
  const getBreadcrumbs = () => {
    if (testSuite) {
      return [
        { label: 'Test Case Generator', href: '/test-case/' },
        { label: testSuite.test_suite_name },
      ]
    }
    return [
      { label: 'Test Case Generator' }
    ]
  }

  // Header actions for results view
  const headerActions = testSuite ? (
    <div className="flex items-center gap-2">
      {cloneData && (
        <button
          onClick={() => {
            navigate('/test-case/', {
              state: {
                cloneFrom: {
                  requirementText: cloneData.requirementText || '',
                  jiraTicket: cloneData.jiraTicket || '',
                  jiraConfirmed: cloneData.jiraConfirmed || false,
                  figmaUrl: cloneData.figmaUrl || '',
                  testCaseCount: cloneData.testCaseCount,
                  autoCount: cloneData.autoCount,
                  detailLevel: cloneData.detailLevel,
                  targetDevices: cloneData.targetDevices,
                  targetBrowsers: cloneData.targetBrowsers,
                  customInstructions: cloneData.customInstructions,
                }
              }
            })
          }}
          className="p-2 text-linear-text-secondary hover:text-linear-text transition-colors rounded-lg hover:bg-linear-bg-hover"
          title="Clone & Edit - create a new generation with pre-filled requirements"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 3h5v5" />
            <path d="M8 3H3v5" />
            <path d="M12 22v-8.3a4 4 0 00-1.172-2.828L3 3" />
            <path d="M15 9l6-6" />
          </svg>
        </button>
      )}
      <button
        onClick={async () => {
          if (!currentJobId) return
          const newStarred = !isStarred
          setIsStarred(newStarred)
          try {
            await toggleStar(currentJobId, newStarred)
            showToast(newStarred ? 'Saved as template' : 'Removed from templates')
          } catch (err) {
            console.error('Star toggle failed:', err)
            setIsStarred(!newStarred)
            showToast('Failed to update template status')
          }
        }}
        className="p-2 text-linear-text-secondary hover:text-linear-yellow transition-colors rounded-lg hover:bg-linear-bg-hover"
        title={isStarred ? 'Remove from templates' : 'Save as template'}
      >
        <svg className="w-5 h-5" fill={isStarred ? '#EAB308' : 'none'} stroke={isStarred ? '#EAB308' : 'currentColor'} viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      </button>
      <button onClick={copyShareUrl} className="btn btn-ghost text-sm">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        Share
      </button>
      <button
        onClick={() => currentJobId && setDeleteModalJobId(currentJobId)}
        className="btn btn-ghost text-sm text-error hover:bg-error/10"
        title="Delete test cases"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Delete
      </button>
      {selectedTestCases.size > 0 && (
        <button
          onClick={handleBulkSaveAsExamples}
          className="btn btn-outline text-[13px] py-1.5 px-3 flex items-center gap-1.5"
          title="Save selected test cases as golden examples for future AI generation"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          Save as Examples
        </button>
      )}
      <div className="relative group">
        <button
          className={`btn btn-primary text-sm flex items-center gap-1 ${selectedTestCases.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={selectedTestCases.size === 0}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {testSuite && selectedTestCases.size < testSuite.test_cases.length
            ? `Export (${selectedTestCases.size})`
            : 'Export'}
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className="dropdown absolute right-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
          <button onClick={() => handleExport('testrail')} className="dropdown-item w-full text-left">
            TestRail CSV
          </button>
          <button onClick={() => handleExport('zephyr')} className="dropdown-item w-full text-left">
            Zephyr Scale CSV
          </button>
          <button onClick={() => handleExport('jira')} className="dropdown-item w-full text-left">
            Jira CSV
          </button>
          <div className="dropdown-divider" />
          <button onClick={() => handleExport('markdown')} className="dropdown-item w-full text-left">
            Markdown
          </button>
          <button onClick={() => handleExport('json')} className="dropdown-item w-full text-left">
            JSON
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setShowHistoryPanel(!showHistoryPanel)}
        className={`p-2 rounded-lg transition-colors ${
          showHistoryPanel
            ? 'text-linear-accent bg-linear-accent/10'
            : 'text-linear-text-secondary hover:text-linear-text hover:bg-linear-bg-hover'
        }`}
        title="Previous Generations"
        aria-label="Previous Generations"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    </div>
  )

  return (
    <AppLayout
      breadcrumbs={getBreadcrumbs()}
      headerActions={headerActions}
    >
      <div {...getRootProps()}>
        <input {...getInputProps()} />

        {/* Not Found State */}
        {isNotFound && (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center flex flex-col items-center">
              <div className="mb-8 relative">
                <div className="w-24 h-24 rounded-full bg-linear-border/30 flex items-center justify-center mx-auto">
                  <svg className="w-16 h-16 text-linear-text-muted opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="absolute inset-0 bg-linear-accent opacity-5 blur-xl rounded-full scale-150 -z-10"></div>
              </div>
              <h2 className="text-2xl font-semibold text-linear-text mb-3 tracking-tight">Test Cases Not Found</h2>
              <p className="text-linear-text-secondary text-[15px] leading-relaxed mb-8 max-w-sm mx-auto">
                This link may have expired or the test cases may no longer exist. Generated test cases are available for 30 days.
              </p>
              <button
                onClick={() => {
                  setIsNotFound(false)
                  setCurrentJobId(null)
                  navigate('/test-case/')
                }}
                className="bg-linear-accent hover:bg-linear-accent-hover text-white px-6 py-3 rounded-md font-medium text-sm inline-flex items-center gap-2 transition-all shadow-[0_1px_2px_rgba(0,0,0,0.2),0_0_0_1px_rgba(87,91,199,0.2)] focus:outline-none focus:ring-2 focus:ring-linear-accent focus:ring-offset-2 focus:ring-offset-linear-bg"
              >
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Generate New Test Cases
              </button>
            </div>
          </div>
        )}

        {!testSuite && !isLoading && !isStreaming && !isNotFound && !isLoadingExisting && (
          <div className="max-w-[1400px] mx-auto space-y-8 pb-24">
            {/* Page Header */}
            <div>
              <h1 className="text-2xl font-semibold text-linear-text mb-1">Test Case Generator</h1>
              <p className="text-linear-text-secondary text-sm">
                Generate comprehensive test suites from requirements, designs, and videos using AI.
              </p>
            </div>

            {/* Main Input Form Card */}
            <div className="border border-linear-border rounded-lg bg-linear-bg-card p-6 space-y-6 shadow-sm">
              {/* Primary Text Area */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-linear-text-secondary uppercase tracking-wide">
                  What needs to be tested?
                </label>
                <textarea
                  value={requirement}
                  onChange={(e) => {
                    setRequirement(e.target.value)
                    clearError()
                  }}
                  className="w-full bg-linear-bg border border-linear-border rounded-md p-4 text-sm text-linear-text placeholder-linear-text-muted focus:border-linear-accent focus:ring-1 focus:ring-linear-accent transition-all resize-y"
                  rows={10}
                  placeholder="Describe the feature requirements, acceptance criteria, or paste user stories here..."
                />
              </div>

              {/* Import from Jira */}
              <div className="space-y-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-linear-text-muted">Import from Jira</span>

                {jiraConfirmed && jiraPreview ? (
                  /* State C: Confirmed — collapsed inline badge */
                  <div className="border-l-2 border-[#2684FF] bg-linear-bg-card border border-linear-border-input rounded-r-lg p-3">
                    <div className="flex items-center gap-3">
                      <JiraIcon />
                      <div
                        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                        onClick={() => setJiraConfirmed(false)}
                      >
                        {(jiraPreview.cloudUrl || jiraStatus?.jiraCloudUrl) ? (
                          <a
                            href={`${jiraPreview.cloudUrl || jiraStatus?.jiraCloudUrl}/browse/${jiraPreview.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-mono text-[#0052CC] hover:underline flex-shrink-0"
                          >
                            {jiraPreview.key}
                          </a>
                        ) : (
                          <span className="text-xs font-mono text-[#0052CC] flex-shrink-0">{jiraPreview.key}</span>
                        )}
                        <span className="text-xs text-linear-text truncate">{jiraPreview.summary}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                          jiraPreview.status.color === 'blue' ? 'bg-info/15 text-info' :
                          jiraPreview.status.color === 'green' ? 'bg-success/15 text-success' :
                          jiraPreview.status.color === 'yellow' ? 'bg-warning/15 text-warning' :
                          'bg-linear-text-muted/20 text-linear-text-muted'
                        }`}>
                          {jiraPreview.status.name}
                        </span>
                      </div>
                      <button
                        onClick={() => { setJiraConfirmed(false); setJiraTicket(''); setJiraPreview(null); setJiraPreviewError(null); setJiraSearchResults([]); setShowSearchDropdown(false) }}
                        className="p-1 rounded text-linear-text-muted hover:text-linear-text hover:bg-linear-bg-hover transition-colors flex-shrink-0"
                        aria-label="Remove Jira ticket"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* State A (Empty) / State B (Preview) / State D (Editing) — input card */
                  <div className="relative border-l-2 border-[#2684FF] bg-linear-bg-card border border-linear-border-input rounded-r-lg p-3">
                    <div className="flex items-center gap-3">
                      <JiraIcon />
                      <input
                        type="text"
                        value={jiraTicket}
                        onChange={(e) => {
                          setJiraTicket(e.target.value)
                          setJiraConfirmed(false)
                          clearError()
                        }}
                        disabled={!jiraStatus || jiraStatus.status === 'disconnected'}
                        className="flex-1 bg-linear-bg border border-linear-border rounded-md px-3 py-2 text-sm text-linear-text placeholder-linear-text-muted focus:border-[#2684FF] focus:ring-1 focus:ring-[#2684FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Search tickets, enter issue key, or paste Jira URL"
                        onFocus={() => {
                          if (jiraSearchResults.length > 0 && !jiraPreview) {
                            setShowSearchDropdown(true)
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow onMouseDown preventDefault on dropdown to fire first
                          setTimeout(() => setShowSearchDropdown(false), 300)
                        }}
                      />
                      {jiraTicket && (
                        <button
                          type="button"
                          onClick={() => { setJiraTicket(''); setJiraPreview(null); setJiraPreviewError(null); setJiraConfirmed(false); setJiraSearchResults([]); setShowSearchDropdown(false) }}
                          className="p-1.5 rounded text-linear-text-muted hover:text-linear-text hover:bg-linear-bg-hover transition-colors"
                          aria-label="Clear Jira ticket"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <p className={`text-[10px] mt-2 ml-[38px] ${jiraPreviewError && !jiraPreviewLoading && !showSearchDropdown ? 'text-error' : 'text-linear-text-muted opacity-60'}`}>
                      {jiraPreviewLoading || jiraSearchLoading ? (
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 border-2 border-[#2684FF]/30 border-t-[#2684FF] rounded-full animate-spin"></span>
                          {jiraPreviewLoading ? `Fetching ${extractJiraKey(jiraTicket) || 'ticket'}...` : 'Searching...'}
                        </span>
                      ) : jiraPreviewError && !showSearchDropdown ? (
                        <span>
                          {jiraPreviewError}{' '}
                          {(jiraStatus?.status === 'expired' || jiraStatus?.status === 'disconnected') && (
                            <Link to="/settings" className="underline hover:text-error transition-colors">Go to Settings</Link>
                          )}
                        </span>
                      ) : (
                        'Pull requirements and acceptance criteria from a Jira ticket'
                      )}
                    </p>

                    {/* Fuzzy search dropdown — z-30 above preview card */}
                    {showSearchDropdown && jiraSearchResults.length > 0 && (
                      <div onMouseDown={(e) => e.preventDefault()} className="absolute w-[55%] min-w-[400px] max-w-[700px] z-30 max-h-[50vh] overflow-y-auto border-l-2 border-[#2684FF] rounded-r-lg shadow-xl shadow-black/40" style={{ top: '58px', left: '46px' }}>
                        {jiraSearchResults.map((result, idx) => (
                          <div
                            key={result.key}
                            className={`group bg-linear-bg-card border border-linear-border-input p-4 ${idx === 0 ? 'rounded-tr-lg' : ''} ${idx === jiraSearchResults.length - 1 && !hasMoreSearchResults ? 'rounded-br-lg' : ''} ${idx > 0 ? 'border-t-0' : ''}`}
                          >
                            {/* Header: key + status + type */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                {jiraStatus?.jiraCloudUrl ? (
                                  <a href={`${jiraStatus.jiraCloudUrl}/browse/${result.key}`} target="_blank" rel="noopener noreferrer"
                                     onClick={(e) => e.stopPropagation()}
                                     className="text-sm font-mono text-[#0052CC] hover:underline">{result.key}</a>
                                ) : (
                                  <span className="text-sm font-mono text-[#0052CC]">{result.key}</span>
                                )}
                                <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                                  result.status.color === 'blue' ? 'bg-info/15 text-info' :
                                  result.status.color === 'green' ? 'bg-success/15 text-success' :
                                  result.status.color === 'yellow' ? 'bg-warning/15 text-warning' :
                                  'bg-linear-text-muted/20 text-linear-text-muted'
                                }`}>{result.status.name}</span>
                              </div>
                              <span className="text-xs text-linear-text-muted px-2 py-0.5 border border-linear-border rounded">{result.type.name}</span>
                            </div>

                            {/* Summary */}
                            <p className="text-[15px] font-medium text-linear-text leading-snug">{result.summary}</p>

                            {/* Toggle chips + Import Ticket on same row */}
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex flex-wrap gap-2">
                                <ToggleChip
                                  label="Description"
                                  checked={searchToggles[result.key]?.description ?? true}
                                  onChange={(v) => setSearchToggles(prev => ({ ...prev, [result.key]: { ...prev[result.key], description: v } }))}
                                  disabled={!result.description}
                                  />
                                <ToggleChip
                                  label={`Attachments${result.attachmentCount ? ` (${result.attachmentCount})` : ''}`}
                                  checked={searchToggles[result.key]?.attachments ?? false}
                                  onChange={(v) => setSearchToggles(prev => ({ ...prev, [result.key]: { ...prev[result.key], attachments: v } }))}
                                  disabled={!result.attachmentCount}
                                  />
                                {result.unsupportedAttachments && (
                                  <span className="text-[10px] text-linear-text-muted italic">
                                    {result.unsupportedAttachments.count} unsupported {result.unsupportedAttachments.count === 1 ? 'file' : 'files'} excluded ({result.unsupportedAttachments.formats.join(', ')})
                                  </span>
                                )}
                              </div>
                              {/* hover-only on desktop, always visible on mobile */}
                              <button
                                onMouseDown={async (e) => {
                                  e.preventDefault()
                                  // Carry over this tile's toggles to the main toggle state
                                  if (searchToggles[result.key]) setJiraFieldToggles(searchToggles[result.key])
                                  skipJiraResetRef.current = true
                                  setJiraTicket(result.key)
                                  setJiraSearchResults([])
                                  setShowSearchDropdown(false)
                                  setJiraPreviewLoading(true)
                                  try {
                                    const preview = await fetchJiraIssue(result.key)
                                    if (!preview.cloudUrl && jiraStatus?.jiraCloudUrl) {
                                      preview.cloudUrl = jiraStatus.jiraCloudUrl
                                    }
                                    setJiraPreview(preview)
                                    setJiraConfirmed(true)
                                  } catch { /* fall back */ }
                                  finally { setJiraPreviewLoading(false) }
                                }}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium bg-[#0052CC] text-white hover:bg-[#003D99] transition-all flex-shrink-0 md:opacity-0 md:group-hover:opacity-100 max-md:opacity-100"
                              >
                                {(!(searchToggles[result.key]?.description ?? true) || !result.description) && (!(searchToggles[result.key]?.attachments ?? false) || !result.attachmentCount) ? 'Link Ticket' : 'Import Ticket'}
                              </button>
                            </div>
                          </div>
                        ))}
                        {hasMoreSearchResults && (
                          <button
                            onMouseDown={async (e) => {
                              e.preventDefault()
                              setLoadingMore(true)
                              try {
                                const { results: moreResults, hasMore } = await searchJiraTickets(jiraTicket.trim(), jiraSearchResults.length)
                                setJiraSearchResults(prev => [...prev, ...moreResults])
                                setHasMoreSearchResults(!!hasMore)
                                // Init toggles for new results
                                const defaultToggles = { description: true, attachments: false }
                                setSearchToggles(prev => {
                                  const next = { ...prev }
                                  moreResults.forEach(r => { if (!next[r.key]) next[r.key] = { ...defaultToggles } })
                                  return next
                                })
                              } catch { /* ignore */ }
                              finally { setLoadingMore(false) }
                            }}
                            disabled={loadingMore}
                            className="w-full bg-linear-bg-card border border-linear-border-input border-t-0 rounded-br-lg p-2.5 text-center text-xs text-[#0052CC] hover:bg-linear-bg-hover transition-colors"
                          >
                            {loadingMore ? 'Loading...' : 'Load More Results'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Not connected banner */}
                    {jiraStatus?.status === 'disconnected' && (
                      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-info/10 border border-info/30 mt-2">
                        <svg className="w-4 h-4 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-info">
                            Connect your Jira account to import tickets directly.{' '}
                            <Link to="/settings" className="underline hover:text-linear-accent transition-colors">Connect in Settings</Link>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Error state — inline inside input card */}

                    {/* Preview card — floats over helper text and content below (State B / State D) */}
                    {jiraPreview && !jiraPreviewLoading && !jiraConfirmed && !showSearchDropdown && (
                      <div className="absolute w-[55%] min-w-[400px] max-w-[700px] z-20 border-l-2 border-[#2684FF] bg-linear-bg-card border border-linear-border-input rounded-r-lg p-4 shadow-xl shadow-black/40" style={{ top: '58px', left: '46px' }}>
                        {/* Header: clickable key + status pill + type floated right */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {(jiraPreview.cloudUrl || jiraStatus?.jiraCloudUrl) ? (
                              <a
                                href={`${jiraPreview.cloudUrl || jiraStatus?.jiraCloudUrl}/browse/${jiraPreview.key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-mono text-[#0052CC] hover:underline"
                              >
                                {jiraPreview.key}
                              </a>
                            ) : (
                              <span className="text-sm font-mono text-linear-text-secondary">{jiraPreview.key}</span>
                            )}
                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                              jiraPreview.status.color === 'blue' ? 'bg-info/15 text-info' :
                              jiraPreview.status.color === 'green' ? 'bg-success/15 text-success' :
                              jiraPreview.status.color === 'yellow' ? 'bg-warning/15 text-warning' :
                              'bg-linear-text-muted/20 text-linear-text-muted'
                            }`}>{jiraPreview.status.name}</span>
                          </div>
                          <span className="text-xs text-linear-text-muted px-2 py-0.5 border border-linear-border rounded">{jiraPreview.type.name}</span>
                        </div>

                        {/* Summary */}
                        <p className="text-[15px] font-medium text-linear-text leading-snug">{jiraPreview.summary}</p>

                        {/* Description preview (2 lines) */}
                        {jiraPreview.description && (
                          <p className="text-xs text-linear-text-muted mt-2 line-clamp-2 leading-relaxed">{jiraPreview.description}</p>
                        )}

                        {/* Toggle chips — Description + Attachments only */}
                        <div className="flex flex-wrap gap-2 mt-4">
                          <ToggleChip
                            label="Description"
                            checked={jiraFieldToggles.description}
                            onChange={(v) => setJiraFieldToggles(prev => ({ ...prev, description: v }))}
                            disabled={!jiraPreview.description}
                          />
                          <ToggleChip
                            label={`Attachments${jiraPreview.attachments?.length ? ` (${jiraPreview.attachments.length})` : ''}`}
                            checked={jiraFieldToggles.attachments}
                            onChange={(v) => setJiraFieldToggles(prev => ({ ...prev, attachments: v }))}
                            disabled={!jiraPreview.attachments?.length}
                          />
                          {jiraPreview.unsupportedAttachments && (
                            <span className="text-[10px] text-linear-text-muted italic">
                              {jiraPreview.unsupportedAttachments.count} unsupported {jiraPreview.unsupportedAttachments.count === 1 ? 'file' : 'files'} excluded ({jiraPreview.unsupportedAttachments.formats.join(', ')})
                            </span>
                          )}
                        </div>

                        {/* Footer with confirm button */}
                        <div className="flex items-center justify-between mt-4">
                          <p className="text-[11px] text-linear-text-muted">Select to include in generation</p>
                          <button
                            onClick={() => setJiraConfirmed(true)}
                            className="bg-[#0052CC] hover:bg-[#003D99] text-white text-sm font-medium px-4 py-1.5 rounded-md transition-colors"
                          >
                            {(!jiraFieldToggles.description || !jiraPreview.description) && (!jiraFieldToggles.attachments || !jiraPreview.attachments?.length) ? 'Link Ticket' : 'Import Ticket'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Side-by-side Visual Reference + Video Reference */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Visual Reference */}
                <div className="space-y-3">
                  <label className="block text-xs font-medium text-linear-text-secondary uppercase tracking-wide flex justify-between">
                    Visual Reference
                    <span className="text-linear-text-muted normal-case text-[10px]">Images & Figma</span>
                  </label>

                  {/* Figma URL Input */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-[14px] h-[14px] text-linear-text-muted" viewBox="0 0 38 57" fill="currentColor">
                        <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z"/>
                        <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z"/>
                        <path d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z"/>
                        <path d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z"/>
                        <path d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z"/>
                      </svg>
                    </div>
                    <input
                      type="url"
                      value={figmaUrl}
                      onChange={(e) => {
                        setFigmaUrl(e.target.value)
                        clearError()
                      }}
                      className="w-full bg-linear-bg border border-linear-border rounded-md pl-10 pr-8 py-2 text-sm text-linear-text placeholder-linear-text-muted focus:border-linear-accent focus:ring-0 transition-colors"
                      placeholder="Paste Figma URL"
                      title="Figma caps design fetches per minute. If a request is rate-limited, the app waits and retries automatically. Linking to a specific node (Copy link to selection) is cheaper than sharing the whole file."
                      aria-label="Figma URL. Figma rate-limits design fetches; the app waits and retries automatically when it happens."
                    />
                    {figmaUrl && (
                      <button
                        onClick={() => setFigmaUrl('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-linear-text-muted hover:text-linear-text transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {figmaUrl && (
                    <p className="text-[10px] text-linear-text-muted mt-1 ml-1">
                      Visual content is interpreted from rendered images. Verify that specific text and numbers in generated test cases match your design.
                    </p>
                  )}

                  {figmaStatus && figmaStatus.status !== 'active' && (
                    <div className="flex items-start gap-2.5 p-3 rounded-lg bg-info/10 border border-info/30 mt-2">
                      <svg className="w-4 h-4 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs text-info">
                          Connect your Figma account to generate test cases from designs.{' '}
                          <Link to="/settings" className="underline hover:text-linear-accent transition-colors">Connect in Settings</Link>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Image Upload Zone */}
                  {uploadedImages.length < MAX_IMAGES && (
                    <div
                      className="border border-dashed border-linear-border rounded-md bg-linear-bg p-4 text-center hover:border-linear-text-muted transition-colors cursor-pointer group"
                      onClick={openFilePicker}
                    >
                      <svg className="w-6 h-6 text-linear-text-muted group-hover:text-linear-text-secondary mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs text-linear-text-muted">Upload screenshots (Max {MAX_IMAGES})</p>
                    </div>
                  )}

                  {/* Uploaded Images List */}
                  {uploadedImages.length > 0 && (
                    <div className="space-y-2">
                      {uploadedImages.map((img, index) => (
                        <div
                          key={index}
                          className="p-2 bg-linear-bg border border-linear-border rounded flex items-center justify-between"
                        >
                          <div className="flex items-center space-x-2 min-w-0">
                            <svg className="w-4 h-4 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs text-linear-text truncate">{img.name}</span>
                          </div>
                          <button
                            onClick={() => removeImage(index)}
                            className="text-linear-text-muted hover:text-error transition-colors flex-shrink-0"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      <p className="text-[10px] text-linear-text-muted">
                        {uploadedImages.length} of {MAX_IMAGES} images
                      </p>
                    </div>
                  )}
                </div>

                {/* Right: Video Reference */}
                <div className="space-y-3">
                  <label className="block text-xs font-medium text-linear-text-secondary uppercase tracking-wide flex justify-between">
                    Video Reference
                    <span className="text-linear-text-muted normal-case text-[10px]">MP4, WebM, MOV</span>
                  </label>

                  <VideoUpload
                    videos={pendingVideos}
                    onVideosChange={setPendingVideos}
                    maxVideos={3}
                    maxSizeMB={500}
                    disabled={isLoading}
                    compact
                  />
                </div>
              </div>

              {/* Output Settings (collapsible) */}
              {(() => {
                const hasCustomSettings = testCaseCount !== 10 || autoCount || detailLevel !== 'standard' || !targetDevices.includes('all') || targetDevices.length !== 1 || !targetBrowsers.includes('all') || targetBrowsers.length !== 1 || customInstructions.trim() !== ''
                const customCount = [
                  testCaseCount !== 10 || autoCount,
                  detailLevel !== 'standard',
                  !targetDevices.includes('all') || targetDevices.length !== 1,
                  !targetBrowsers.includes('all') || targetBrowsers.length !== 1,
                  customInstructions.trim() !== '',
                ].filter(Boolean).length

                const resetToDefaults = () => {
                  setTestCaseCount(10)
                  setAutoCount(false)
                  setDetailLevel('standard')
                  setTargetDevices(['all'])
                  setTargetBrowsers(['all'])
                  setCustomInstructions('')
                }

                return (
                  <div className="border border-linear-border rounded-md bg-linear-bg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setConfigOpen(!configOpen)}
                      className="flex items-center justify-between w-full p-4 cursor-pointer hover:bg-linear-bg-hover transition-colors text-left"
                      aria-expanded={configOpen}
                      aria-controls="output-settings-content"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-linear-text-secondary">Output Settings</span>
                        {hasCustomSettings && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-linear-accent/20 text-linear-accent text-[10px] font-semibold">
                            {customCount}
                          </span>
                        )}
                      </div>
                      <svg className={`w-5 h-5 text-linear-text-muted transform transition-transform ${configOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {configOpen && (
                      <div id="output-settings-content" className="p-4 border-t border-linear-border grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {/* Left: Test Case Count (most changed) */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              <label className="font-medium text-linear-text-muted">{autoCount ? 'Test Cases' : 'Approximate Test Cases'}</label>
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={autoCount}
                                  aria-label="Let AI decide test case count"
                                  onClick={() => { setAutoCount(!autoCount); clearError() }}
                                  className={`relative inline-flex h-3.5 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-linear-accent focus:ring-offset-1 ${
                                    autoCount ? 'bg-linear-accent' : 'bg-linear-border'
                                  }`}
                                >
                                  <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                                    autoCount ? 'translate-x-3.5' : 'translate-x-0.5'
                                  }`} />
                                </button>
                                <span className="text-[10px] text-linear-text-muted">Let AI decide</span>
                                <span className="relative group">
                                  <svg className="w-3 h-3 text-linear-text-muted cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                    <path strokeWidth="2" strokeLinecap="round" d="M12 16v-1m0-3a2 2 0 10-2-2" />
                                    <circle cx="12" cy="16" r="0.5" fill="currentColor" />
                                  </svg>
                                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] bg-linear-bg-card border border-linear-border rounded shadow-lg text-linear-text-muted w-48 hidden group-hover:block z-10">
                                    When on, the AI analyzes your requirements and picks the right number of test cases. Turn off to set the count manually.
                                  </span>
                                </span>
                              </div>
                            </div>
                            <span className="font-mono text-linear-accent">
                              {autoCount ? 'Auto' : testCaseCount}
                            </span>
                          </div>
                          <input
                            type="range"
                            min={3}
                            max={20}
                            value={testCaseCount}
                            onChange={(e) => { setTestCaseCount(Number(e.target.value)); clearError() }}
                            disabled={autoCount}
                            className={`w-full h-1 bg-linear-border rounded-lg appearance-none cursor-pointer accent-linear-accent transition-opacity ${
                              autoCount ? 'opacity-30 pointer-events-none' : ''
                            }`}
                          />
                          <div className={`flex justify-between text-[10px] text-linear-text-muted transition-opacity ${autoCount ? 'opacity-30' : ''}`}>
                            <span>Focused (3)</span>
                            <span>Comprehensive (20)</span>
                          </div>
                        </div>

                        {/* Right: Step Detail Level */}
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-linear-text-muted">Step Detail Level</label>
                          <div className="flex bg-linear-bg-card p-1 rounded-md border border-linear-border">
                            {(['concise', 'standard', 'detailed'] as const).map((level) => (
                              <button
                                key={level}
                                type="button"
                                className={`flex-1 py-1.5 text-[11px] rounded transition-colors ${
                                  detailLevel === level
                                    ? 'bg-linear-border text-linear-text shadow-sm font-medium'
                                    : 'text-linear-text-muted hover:text-linear-text'
                                }`}
                                onClick={() => { setDetailLevel(level); clearError() }}
                              >
                                {level.charAt(0).toUpperCase() + level.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Left: Target Devices */}
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-linear-text-muted">Target Devices</label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className={`px-3 py-1 rounded-full text-[11px] font-medium cursor-pointer transition-colors border ${
                                targetDevices.includes('all')
                                  ? 'border-linear-accent bg-linear-accent/10 text-linear-accent'
                                  : 'border-linear-border bg-linear-bg-card text-linear-text-muted hover:border-linear-text-secondary'
                              }`}
                              onClick={() => setTargetDevices(['all'])}
                            >
                              All Devices
                            </button>
                            {DEVICE_OPTIONS.map((device) => (
                              <button
                                key={device}
                                type="button"
                                className={`px-3 py-1 rounded-full text-[11px] font-medium cursor-pointer transition-colors border ${
                                  targetDevices.includes(device)
                                    ? 'border-linear-accent bg-linear-accent/10 text-linear-accent'
                                    : 'border-linear-border bg-linear-bg-card text-linear-text-muted hover:border-linear-text-secondary'
                                }`}
                                onClick={() => {
                                  setTargetDevices((prev) => {
                                    const filtered = prev.filter((d) => d !== 'all')
                                    if (filtered.includes(device)) {
                                      const result = filtered.filter((d) => d !== device)
                                      return result.length === 0 ? ['all'] : result
                                    }
                                    return [...filtered, device]
                                  })
                                }}
                              >
                                {device}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Right: Target Browsers */}
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-linear-text-muted">Target Browsers</label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className={`px-3 py-1 rounded-full text-[11px] font-medium cursor-pointer transition-colors border ${
                                targetBrowsers.includes('all')
                                  ? 'border-linear-accent bg-linear-accent/10 text-linear-accent'
                                  : 'border-linear-border bg-linear-bg-card text-linear-text-muted hover:border-linear-text-secondary'
                              }`}
                              onClick={() => setTargetBrowsers(['all'])}
                            >
                              All Browsers
                            </button>
                            {BROWSER_OPTIONS.map((browser) => (
                              <button
                                key={browser}
                                type="button"
                                className={`px-3 py-1 rounded-full text-[11px] font-medium cursor-pointer transition-colors border ${
                                  targetBrowsers.includes(browser)
                                    ? 'border-linear-accent bg-linear-accent/10 text-linear-accent'
                                    : 'border-linear-border bg-linear-bg-card text-linear-text-muted hover:border-linear-text-secondary'
                                }`}
                                onClick={() => {
                                  setTargetBrowsers((prev) => {
                                    const filtered = prev.filter((b) => b !== 'all')
                                    if (filtered.includes(browser)) {
                                      const result = filtered.filter((b) => b !== browser)
                                      return result.length === 0 ? ['all'] : result
                                    }
                                    return [...filtered, browser]
                                  })
                                }}
                              >
                                {browser}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Full width: Custom Instructions */}
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs font-medium text-linear-text-muted">Custom Instructions</label>
                          <textarea
                            value={customInstructions}
                            onChange={(e) => {
                              if (e.target.value.length <= 2000) {
                                setCustomInstructions(e.target.value)
                                clearError()
                              }
                            }}
                            rows={3}
                            className="w-full bg-linear-bg-card border border-linear-border rounded px-3 py-2 text-sm text-linear-text focus:ring-1 focus:ring-linear-accent placeholder-linear-text-muted transition-colors resize-y"
                            placeholder="e.g., Combine related scenarios into fewer, longer test cases. More than 5 steps per test is acceptable."
                          />
                          <div className="flex justify-between">
                            <p className="text-[10px] text-linear-text-muted opacity-60">Override default behavior like step count, scenario combining, or focus areas</p>
                            <span className={`text-[10px] ${customInstructions.length > 1800 ? 'text-error' : 'text-linear-text-muted opacity-60'}`}>
                              {customInstructions.length}/2000
                            </span>
                          </div>
                        </div>

                        {/* Reset to defaults */}
                        {hasCustomSettings && (
                          <div className="md:col-span-2">
                            <button
                              type="button"
                              onClick={resetToDefaults}
                              className="text-[11px] text-linear-text-muted hover:text-linear-accent transition-colors"
                            >
                              Reset to defaults
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Golden examples active indicator */}
              {goldenExamplesCount > 0 && (
                <div className="flex items-center gap-1.5 text-[12px] text-linear-text-muted mt-1" title="These examples guide AI output style. Manage in Settings.">
                  <svg className="w-3.5 h-3.5 text-linear-yellow" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  {goldenExamplesCount} golden example{goldenExamplesCount !== 1 ? 's' : ''} active
                </div>
              )}

              {/* Drag & Drop Overlay */}
              {isDragActive && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                  <div className="w-full max-w-2xl mx-4">
                    <div className="relative bg-linear-bg-card border-2 border-dashed border-linear-accent rounded-xl p-12 flex flex-col items-center justify-center text-center shadow-2xl shadow-linear-accent/10">
                      <div className="h-24 w-24 bg-linear-accent/10 rounded-full flex items-center justify-center mb-6">
                        <svg className="w-12 h-12 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-semibold text-linear-text mb-2">
                        Drop to upload images or videos
                      </h2>
                      <p className="text-linear-text-secondary text-base mb-6 max-w-md">
                        Release your files here to attach them to the current test case generation request.
                      </p>
                      <div className="flex flex-wrap justify-center gap-2 mb-4">
                        {['PNG', 'JPG', 'MP4', 'MOV', 'WebM'].map((fmt) => (
                          <span key={fmt} className="px-2.5 py-1 rounded-md bg-linear-bg-button text-linear-text-muted text-xs font-medium uppercase tracking-wide">
                            {fmt}
                          </span>
                        ))}
                      </div>
                      <div className="text-linear-text-muted text-sm flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Max 10MB
                        </span>
                        <span className="w-1 h-1 rounded-full bg-linear-border-input"></span>
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Max 500MB
                        </span>
                      </div>
                      <div className="absolute inset-0 pointer-events-none rounded-xl bg-linear-accent/[0.03]"></div>
                    </div>
                  </div>
                </div>
              )}

              {error ? (
                <div
                  role={isRateLimited ? 'alert' : undefined}
                  aria-live={isRateLimited ? 'assertive' : undefined}
                  className={`p-4 border rounded-lg text-sm break-words whitespace-normal leading-relaxed ${
                    isRateLimited
                      ? 'bg-warning/10 border-warning/30 text-warning'
                      : 'bg-error/10 border-error/30 text-error'
                  }`}
                >
                  {error}
                </div>
              ) : null}

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                className="w-full bg-linear-text hover:bg-white text-linear-bg py-3 px-4 rounded-md font-semibold text-sm transition-colors shadow-lg shadow-linear-text/10 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-linear-text"
                disabled={isLoading || (!requirement.trim() && uploadedImages.length === 0 && !figmaUrl.trim() && !(jiraConfirmed && jiraPreview))}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5z" />
                </svg>
                Generate Test Cases
              </button>
            </div>

          </div>
        )}

        {/* Loading State */}
        {(isLoading || isStreaming) && (
          <div className="w-full max-w-[1400px] mx-auto flex flex-col items-center justify-center">
            <div className="w-full flex flex-col gap-6 animate-fade-in">
              {isStreaming ? (
                <>
                  {/* Streaming State */}
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-linear-bg-card rounded-full border border-linear-border shadow-sm mb-2">
                      <div className="relative w-2.5 h-2.5">
                        <div className="w-2.5 h-2.5 bg-success rounded-full" />
                        <div className="pulse-ring" />
                      </div>
                      <span className="text-xs font-medium text-success tracking-wide uppercase">Generating Live</span>
                    </div>
                    <h2 className="text-2xl font-semibold text-linear-text">Crafting Test Cases</h2>
                    <p className="text-linear-text-muted text-sm">Analyzing requirements and structuring scenarios...</p>
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-2">
                    <div className="w-full bg-linear-bg-card rounded-full h-1.5 overflow-hidden border border-linear-border">
                      <div
                        className="h-full bg-linear-accent rounded-full relative transition-all duration-[2000ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                        style={{ width: `${Math.min(95, (streamedText.length / 15000) * 100)}%` }}
                      >
                        <div className="absolute inset-0 progress-bar-shimmer" />
                      </div>
                    </div>
                  </div>

                  {/* Code preview */}
                  <div className="bg-linear-bg border border-linear-border rounded-lg shadow-2xl overflow-hidden flex flex-col h-[400px]">
                    <div className="flex-1 relative overflow-hidden">
                      <pre
                        ref={streamingOutputRef}
                        className="h-full p-6 font-mono text-sm overflow-y-auto text-linear-text-secondary leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{
                          __html: streamedText
                            ? colorizeJson(streamedText) + '<span class="inline-block w-2 h-4 bg-[var(--linear-accent)] align-middle ml-1 animate-pulse"></span>'
                            : '<span class="text-linear-text-muted">Waiting for response...</span>'
                        }}
                      />
                      <div className="absolute bottom-4 right-4 bg-linear-bg-card/90 backdrop-blur px-3 py-1.5 rounded-md text-[11px] text-linear-text-muted border border-linear-border font-mono shadow-md">
                        {(streamedText.length / 1000).toFixed(1)}k chars
                      </div>
                    </div>
                  </div>

                  {/* Cancel button */}
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={handleCancelGeneration}
                      className="px-6 py-2 rounded-md border border-linear-border hover:border-error/50 hover:bg-error/10 hover:text-error text-linear-text-secondary text-sm font-medium transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      Cancel Generation
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Polling State */}
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-linear-bg-card rounded-full border border-linear-border shadow-sm mb-2">
                      <div className="spinner spinner-sm" />
                    </div>
                    <h2 className="text-2xl font-semibold text-linear-text">Generating Test Cases...</h2>
                    <p className="text-linear-text-muted text-sm">
                      {statusMessage || 'Processing your requirements...'}
                    </p>
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-2">
                    <div className="w-full bg-linear-bg-card rounded-full h-1.5 overflow-hidden border border-linear-border">
                      <div
                        className="h-full bg-linear-accent rounded-full relative transition-all duration-[2000ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                        style={{ width: `${progress}%` }}
                      >
                        <div className="absolute inset-0 progress-bar-shimmer" />
                      </div>
                    </div>
                  </div>

                  {/* Cancel button */}
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={handleCancelGeneration}
                      className="px-6 py-2 rounded-md border border-linear-border hover:border-error/50 hover:bg-error/10 hover:text-error text-linear-text-secondary text-sm font-medium transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      Cancel Generation
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {testSuite && !isLoading && !isStreaming && (() => {
          const happyCount = testSuite.test_cases.filter(tc => tc.type === 'happy_path').length
          const sadCount = testSuite.test_cases.filter(tc => tc.type === 'sad_path').length
          const edgeCount = testSuite.test_cases.filter(tc => tc.type === 'edge_case').length
          const total = testSuite.test_cases.length || 1

          return (
          <div className="max-w-[1400px] mx-auto space-y-8 pb-10">
            {/* Summary */}
            <section className="bg-linear-bg-sidebar border border-linear-border rounded-lg p-6 shadow-sm">
              <div className="flex flex-col md:flex-row gap-6 justify-between items-start">
                {/* Left: Title + description + metadata */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-linear-text">{testSuite.test_suite_name}</h1>
                    {jiraTicket && <span className="badge badge-info font-mono">{jiraTicket}</span>}
                  </div>
                  {testSuite.requirement && (
                    <div className="text-sm text-linear-text-secondary max-w-2xl markdown-preview [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h2]:font-semibold [&_h3]:font-medium [&_p]:mb-2 [&_ul]:mb-2 [&_li]:text-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {testSuite.requirement}
                      </ReactMarkdown>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-xs text-linear-text-muted">
                    {jobCreator?.userName && (
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Created by {jobCreator.userName}
                      </div>
                    )}
                    {jobCreator?.createdAt && (
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {(() => {
                          const date = new Date(jobCreator.createdAt)
                          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                          const tz = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || ''
                          return `${dateStr}, ${timeStr} ${tz}`
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Coverage Stats box */}
                <div className="flex-shrink-0 bg-linear-bg rounded-lg border border-linear-border p-4 w-full md:w-auto min-w-[280px]">
                  <h3 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-3">Coverage Stats</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-bold text-linear-text">{testSuite.test_cases.length}</div>
                      <div className="text-xs text-linear-text-muted">Total Cases</div>
                    </div>
                    <div className="space-y-1">
                      {/* Happy Path bar */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-success">Happy Path</span>
                        <span className="font-medium text-linear-text">{happyCount}</span>
                      </div>
                      <div className="h-1.5 bg-linear-bg-card rounded-full overflow-hidden">
                        <div className="h-full bg-success rounded-full" style={{ width: `${(happyCount / total) * 100}%` }}></div>
                      </div>
                      {/* Sad Path bar */}
                      <div className="flex items-center justify-between text-xs pt-1">
                        <span className="text-error">Sad Path</span>
                        <span className="font-medium text-linear-text">{sadCount}</span>
                      </div>
                      <div className="h-1.5 bg-linear-bg-card rounded-full overflow-hidden">
                        <div className="h-full bg-error rounded-full" style={{ width: `${(sadCount / total) * 100}%` }}></div>
                      </div>
                      {/* Edge Case bar */}
                      <div className="flex items-center justify-between text-xs pt-1">
                        <span className="text-warning">Edge Case</span>
                        <span className="font-medium text-linear-text">{edgeCount}</span>
                      </div>
                      <div className="h-1.5 bg-linear-bg-card rounded-full overflow-hidden">
                        <div className="h-full bg-warning rounded-full" style={{ width: `${(edgeCount / total) * 100}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Reference Videos */}
            {(jobVideos.length > 0 || isUploadingVideos) && (
              <section>
                <h2 className="text-sm font-semibold text-linear-text-muted mb-3 flex items-center gap-2">
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Reference Materials
                  {isUploadingVideos && <span className="spinner spinner-sm ml-2"></span>}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {jobVideos.map((video) => (
                    <div key={video.videoId}
                      className="group relative aspect-video bg-linear-bg-sidebar border border-linear-border rounded-lg overflow-hidden cursor-pointer hover:border-linear-border-input transition-all"
                      onClick={() => setPlayingVideo(video)}>
                      {/* Play overlay on hover */}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                      </div>
                      {/* Dark placeholder bg */}
                      <div className="absolute inset-0 bg-linear-bg-card flex items-center justify-center">
                        <svg className="w-12 h-12 text-linear-text-muted opacity-20" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                      {/* Bottom gradient with filename */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent z-10">
                        <div className="text-xs font-medium text-white truncate">{video.fileName}</div>
                        <div className="text-[10px] text-white/70">
                          {video.fileSize ? `${(video.fileSize / (1024*1024)).toFixed(1)}MB` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* [VERIFY] Resolution Banner */}
            {!verifyBannerDismissed && verifyCount > 0 && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-warning/10 border border-warning/30">
                <svg className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-warning font-medium">
                    {verifyCount} test case{verifyCount !== 1 ? 's have' : ' has'} uncertain expected results
                  </p>
                  <p className="text-xs text-warning/80 mt-1">
                    Items marked with <span className="px-1 py-0.5 rounded bg-warning/20 text-warning text-xs font-medium">[VERIFY]</span> need review. Click the tag to edit inline, or export as-is and tags will be stripped automatically.
                  </p>
                </div>
                <button
                  onClick={() => setVerifyBannerDismissed(true)}
                  className="text-warning/70 hover:text-warning transition-colors flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Test Cases */}
            <section className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-linear-text-muted flex items-center gap-2">
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Generated Test Cases
                  {verifyCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-warning/15 text-warning border border-warning/30">
                      {verifyCount} to verify
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-3">
                  {verifyCount > 0 && (
                    <button
                      onClick={() => setShowVerifyOnly(!showVerifyOnly)}
                      className={`text-xs px-2 py-1 rounded transition-colors ${
                        showVerifyOnly
                          ? 'bg-warning/15 text-warning border border-warning/30'
                          : 'text-linear-text-secondary hover:text-linear-text hover:bg-linear-bg-hover'
                      }`}
                    >
                      {showVerifyOnly ? 'Show all' : 'Show [VERIFY] only'}
                    </button>
                  )}
                  <label className="flex items-center gap-2 text-xs text-linear-text-secondary cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedTestCases.size === testSuite.test_cases.length}
                      ref={(el) => {
                        if (el) el.indeterminate = selectedTestCases.size > 0 && selectedTestCases.size < testSuite.test_cases.length
                      }}
                      onChange={() => {
                        if (selectedTestCases.size === testSuite.test_cases.length) {
                          setSelectedTestCases(new Set())
                        } else {
                          setSelectedTestCases(new Set(testSuite.test_cases.map((tc) => tc.id)))
                        }
                      }}
                      className="w-4 h-4 rounded border-linear-border text-linear-accent focus:ring-linear-accent cursor-pointer"
                    />
                    {selectedTestCases.size === testSuite.test_cases.length
                      ? 'All selected'
                      : `${selectedTestCases.size} of ${testSuite.test_cases.length} selected`}
                  </label>
                </div>
              </div>
              {sortTestCases(
                showVerifyOnly
                  ? testSuite.test_cases.filter((tc) => hasVerifyTags(tc))
                  : testSuite.test_cases
              ).map((tc, idx) => renderTestCase(tc, idx))}
            </section>

            {/* New Generation CTA */}
            <div className="flex justify-center pt-6 pb-20">
              <button
                onClick={() => {
                  setTestSuite(null)
                  setCurrentJobId(null)
                  setJobCreator(null)
                  setCloneData(null)
                  setIsStarred(false)
                  setEditingTestCase(null)
                  setEditedTestCases(new Map())
                  setRequirement('')
                  setFigmaUrl('')
                  setUploadedImages([])
                  setPendingVideos([])
                  setJobVideos([])
                  setJiraTicket('')
                  resetStreaming()
                  navigate('/test-case/')
                }}
                className="flex items-center gap-2 bg-linear-accent hover:bg-linear-accent-hover text-white px-6 py-3 rounded-md text-sm font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" /></svg>
                Generate New Test Cases
              </button>
            </div>
          </div>
          )
        })()}

        {/* Delete Confirmation Modal */}
        {deleteModalJobId && (
          <div className="modal-overlay" onClick={() => !isDeleting && setDeleteModalJobId(null)}>
            <div className="modal max-w-md w-full" role="dialog" aria-modal="true" aria-label="Delete test cases" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-end p-2">
                <button
                  onClick={() => setDeleteModalJobId(null)}
                  className="p-1 text-linear-text-muted hover:text-linear-text transition-colors disabled:text-linear-border disabled:cursor-not-allowed"
                  disabled={isDeleting}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 pb-6 pt-0">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-linear-text mb-2">Delete Test Cases</h3>
                    <p className="text-sm text-linear-text-secondary leading-relaxed">
                      Are you sure you want to delete {testSuite?.test_suite_name ? <><span className="font-medium text-linear-text">"{testSuite.test_suite_name}"</span>? </> : 'this test suite? '}This action cannot be undone and all associated test data will be permanently removed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-linear-bg-hover">
                <button
                  onClick={() => setDeleteModalJobId(null)}
                  className="btn btn-outline"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteTestCase(deleteModalJobId)}
                  className="btn btn-danger"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <span className="flex items-center">
                      <div className="spinner spinner-sm mr-2"></div>
                      Deleting...
                    </span>
                  ) : (
                    'Delete Test Cases'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <div className="fixed bottom-4 right-4 toast toast-success animate-fade-in z-50">
            <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-linear-text">{toast}</span>
          </div>
        )}

        {/* Video Player Modal */}
        {playingVideo && (
          <VideoPlayer
            videoUrl={playingVideo.downloadUrl}
            fileName={playingVideo.fileName}
            fileSize={playingVideo.fileSize}
            onClose={() => setPlayingVideo(null)}
          />
        )}

        {/* History Slide-out Panel */}
        {showHistoryPanel && (
          <HistoryPanel title="Previous Generations" onClose={() => setShowHistoryPanel(false)}>
            <TestCaseHistory
              onDeleteRequest={(jobId) => setDeleteModalJobId(jobId)}
              onSelectJob={() => setShowHistoryPanel(false)}
              embedded
              deletedJobId={lastDeletedJobId}
            />
          </HistoryPanel>
        )}
      </div>
    </AppLayout>
  )
}
